/**
 * Persian formatting.
 *
 * Dates and counts are shown in the Jalali calendar with Persian digits
 * (`Intl` `fa-IR`) because that is what the NOC reads; machine identifiers stay
 * Latin and are isolated LTR by the `.mono` class. Never mix the two in one
 * formatter.
 */

const dateTimeNumeric = new Intl.DateTimeFormat('fa-IR', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const dateNumeric = new Intl.DateTimeFormat('fa-IR', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const timeOnly = new Intl.DateTimeFormat('fa-IR', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const decimal = new Intl.NumberFormat('fa-IR');

const relative = new Intl.RelativeTimeFormat('fa', { numeric: 'auto' });

export const NA = '—';

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** `۱۴۰۵/۰۷/۱۸، ۱۰:۱۲` — the default cell for timestamps. */
export function formatDateTime(value: string | null | undefined): string {
  const date = toDate(value);
  return date ? dateTimeNumeric.format(date) : NA;
}

export function formatDate(value: string | null | undefined): string {
  const date = toDate(value);
  return date ? dateNumeric.format(date) : NA;
}

export function formatTime(value: string | null | undefined): string {
  const date = toDate(value);
  return date ? timeOnly.format(date) : NA;
}

export function formatNumber(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? decimal.format(value) : NA;
}

/** Signed value for optical power and deltas: `−۱۹٫۴ dBm`. */
export function formatDbm(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return NA;
  const sign = value < 0 ? '−' : '+';
  return `${sign}${decimal.format(Math.abs(value))} dBm`;
}

/**
 * A firmware image the size the vendor sheet gives it: `۲۸٫۴ MiB`. The exact byte count is
 * a separate attribute on the row — two images that both read «28 MiB» are not the same file,
 * and a page must not round a fact the operator may need to compare.
 */
export function formatMiB(bytes: number | null | undefined): string {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes)) return NA;
  return `${decimal.format(Number((bytes / 1_048_576).toFixed(1)))} MiB`;
}

/** Compact duration in minutes → `۲ ساعت و ۱۴ دقیقه`. */
export function formatDurationMinutes(total: number): string {
  if (!Number.isFinite(total) || total < 0) return NA;
  const minutes = Math.round(total);
  if (minutes < 60) return `${decimal.format(minutes)} دقیقه`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours < 24) {
    return rest === 0
      ? `${decimal.format(hours)} ساعت`
      : `${decimal.format(hours)} ساعت و ${decimal.format(rest)} دقیقه`;
  }
  const days = Math.floor(hours / 24);
  return `${decimal.format(days)} روز`;
}

export function minutesSince(value: string | null | undefined, now = Date.now()): number | null {
  const date = toDate(value);
  if (!date) return null;
  return (now - date.getTime()) / 60_000;
}

/**
 * `۳ دقیقه پیش` / `پریروز` — the freshness column an operator scans.
 * A missing timestamp is rendered as the ZTP phrase, not `NaN پیش`.
 */
export function formatRelative(value: string | null | undefined, now = Date.now()): string {
  const date = toDate(value);
  if (!date) return 'هرگز';
  const seconds = Math.round((date.getTime() - now) / 1000);
  const abs = Math.abs(seconds);
  if (abs < 45) return relative.format(Math.round(seconds), 'second');
  if (abs < 45 * 60) return relative.format(Math.round(seconds / 60), 'minute');
  if (abs < 45 * 60 * 24) return relative.format(Math.round(seconds / 3600), 'hour');
  if (abs < 7 * 24 * 3600) return relative.format(Math.round(seconds / 86_400), 'day');
  if (abs < 30 * 24 * 3600) return relative.format(Math.round(seconds / 604_800), 'week');
  return relative.format(Math.round(seconds / 2_592_000), 'month');
}

/** Last 4 characters of a serial — used for the human "short id" label. */
export function serialTail(serial: string): string {
  return serial.length <= 4 ? serial : serial.slice(-4);
}

export function deviceShortId(acsDeviceId: string): string {
  const serial = acsDeviceId.startsWith('acs-device:')
    ? acsDeviceId.slice('acs-device:'.length)
    : acsDeviceId;
  return serial;
}

/** `acs-device:` prefix stripping for compact columns. */
export function stripDevicePrefix(acsDeviceId: string): string {
  return acsDeviceId.startsWith('acs-device:')
    ? acsDeviceId.slice('acs-device:'.length)
    : acsDeviceId;
}
